import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Star, Calendar, Moon, Eye, Clock, ChevronRight, Sparkles
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

// Major meteor showers data
const METEOR_SHOWERS = [
  {
    id: 'quadrantids',
    name: 'Quadrantids',
    namePL: 'Kwadrantydy',
    peak: { month: 1, day: 3 },
    active: { start: { month: 12, day: 28 }, end: { month: 1, day: 12 } },
    zhr: 120,
    speed: 41,
    radiant: { ra: 230, dec: 49 },
    parent: '2003 EH1 (asteroid)',
    description: 'Strong shower with sharp peak lasting only a few hours',
    descriptionPL: 'Silny rój z ostrym szczytem trwającym tylko kilka godzin'
  },
  {
    id: 'lyrids',
    name: 'Lyrids',
    namePL: 'Lirydy',
    peak: { month: 4, day: 22 },
    active: { start: { month: 4, day: 16 }, end: { month: 4, day: 25 } },
    zhr: 18,
    speed: 49,
    radiant: { ra: 271, dec: 34 },
    parent: 'C/1861 G1 Thatcher',
    description: 'One of the oldest known meteor showers',
    descriptionPL: 'Jeden z najstarszych znanych rojów meteorów'
  },
  {
    id: 'eta-aquariids',
    name: 'Eta Aquariids',
    namePL: 'Eta Akwarydy',
    peak: { month: 5, day: 6 },
    active: { start: { month: 4, day: 19 }, end: { month: 5, day: 28 } },
    zhr: 50,
    speed: 66,
    radiant: { ra: 338, dec: -1 },
    parent: '1P/Halley',
    description: 'Fast meteors from Halley\'s Comet, best seen from Southern Hemisphere',
    descriptionPL: 'Szybkie meteory z komety Halleya, najlepiej widoczne z półkuli południowej'
  },
  {
    id: 'delta-aquariids',
    name: 'Delta Aquariids',
    namePL: 'Delta Akwarydy',
    peak: { month: 7, day: 30 },
    active: { start: { month: 7, day: 12 }, end: { month: 8, day: 23 } },
    zhr: 20,
    speed: 41,
    radiant: { ra: 340, dec: -16 },
    parent: '96P/Machholz',
    description: 'Steady shower, good warm-up for Perseids',
    descriptionPL: 'Stabilny rój, dobra rozgrzewka przed Perseidami'
  },
  {
    id: 'perseids',
    name: 'Perseids',
    namePL: 'Perseidy',
    peak: { month: 8, day: 12 },
    active: { start: { month: 7, day: 17 }, end: { month: 8, day: 24 } },
    zhr: 100,
    speed: 59,
    radiant: { ra: 48, dec: 58 },
    parent: '109P/Swift-Tuttle',
    description: 'Most popular meteor shower, bright meteors with persistent trains',
    descriptionPL: 'Najpopularniejszy rój meteorów, jasne meteory z długimi śladami'
  },
  {
    id: 'orionids',
    name: 'Orionids',
    namePL: 'Orionidy',
    peak: { month: 10, day: 21 },
    active: { start: { month: 10, day: 2 }, end: { month: 11, day: 7 } },
    zhr: 20,
    speed: 66,
    radiant: { ra: 95, dec: 16 },
    parent: '1P/Halley',
    description: 'Fast meteors from Halley\'s Comet',
    descriptionPL: 'Szybkie meteory z komety Halleya'
  },
  {
    id: 'leonids',
    name: 'Leonids',
    namePL: 'Leonidy',
    peak: { month: 11, day: 17 },
    active: { start: { month: 11, day: 6 }, end: { month: 11, day: 30 } },
    zhr: 15,
    speed: 71,
    radiant: { ra: 152, dec: 22 },
    parent: '55P/Tempel-Tuttle',
    description: 'Very fast meteors, occasional storms every 33 years',
    descriptionPL: 'Bardzo szybkie meteory, sporadyczne burze co 33 lata'
  },
  {
    id: 'geminids',
    name: 'Geminids',
    namePL: 'Geminidy',
    peak: { month: 12, day: 14 },
    active: { start: { month: 12, day: 4 }, end: { month: 12, day: 20 } },
    zhr: 150,
    speed: 35,
    radiant: { ra: 112, dec: 33 },
    parent: '3200 Phaethon (asteroid)',
    description: 'King of meteor showers, bright and numerous meteors',
    descriptionPL: 'Król rojów meteorów, jasne i liczne meteory'
  },
  {
    id: 'ursids',
    name: 'Ursids',
    namePL: 'Ursydy',
    peak: { month: 12, day: 22 },
    active: { start: { month: 12, day: 17 }, end: { month: 12, day: 26 } },
    zhr: 10,
    speed: 33,
    radiant: { ra: 217, dec: 76 },
    parent: '8P/Tuttle',
    description: 'Modest shower near winter solstice',
    descriptionPL: 'Skromny rój w okolicach przesilenia zimowego'
  }
];

const MeteorShowers = ({ latitude, longitude }) => {
  const { t } = useI18n();
  const [selectedShower, setSelectedShower] = useState(null);
  const [year] = useState(new Date().getFullYear());

  // Calculate moon phase for a given date (simplified)
  const getMoonPhase = (date) => {
    const lp = 2551443; // lunar period in seconds
    const newMoon = new Date(1970, 0, 7, 20, 35, 0).getTime() / 1000;
    const phase = ((date.getTime() / 1000 - newMoon) % lp) / lp;
    return phase;
  };

  // Get moon illumination percentage
  const getMoonIllumination = (phase) => {
    if (phase < 0.5) {
      return Math.round(phase * 2 * 100);
    } else {
      return Math.round((1 - (phase - 0.5) * 2) * 100);
    }
  };

  // Calculate visibility quality based on moon
  const getVisibilityQuality = (shower) => {
    const peakDate = new Date(year, shower.peak.month - 1, shower.peak.day);
    const moonPhase = getMoonPhase(peakDate);
    const illumination = getMoonIllumination(moonPhase);
    
    // Lower moon = better visibility
    if (illumination < 20) return { quality: t('shotScore.excellent'), color: 'text-emerald-500', bg: 'bg-emerald-500/20', score: 95 };
    if (illumination < 40) return { quality: t('meteor.veryGood'), color: 'text-green-500', bg: 'bg-green-500/20', score: 80 };
    if (illumination < 60) return { quality: t('shotScore.good'), color: 'text-yellow-500', bg: 'bg-yellow-500/20', score: 60 };
    if (illumination < 80) return { quality: t('shotScore.fair'), color: 'text-orange-500', bg: 'bg-orange-500/20', score: 40 };
    return { quality: t('shotScore.poor'), color: 'text-red-500', bg: 'bg-red-500/20', score: 20 };
  };

  // Sort showers by upcoming dates
  const sortedShowers = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentDay = now.getDate();

    return [...METEOR_SHOWERS].sort((a, b) => {
      const aMonth = a.peak.month;
      const bMonth = b.peak.month;
      
      // Adjust for year wrap-around
      const aAdjusted = aMonth < currentMonth || (aMonth === currentMonth && a.peak.day < currentDay) 
        ? aMonth + 12 : aMonth;
      const bAdjusted = bMonth < currentMonth || (bMonth === currentMonth && b.peak.day < currentDay)
        ? bMonth + 12 : bMonth;
      
      return aAdjusted - bAdjusted || a.peak.day - b.peak.day;
    });
  }, []);

  // Get next upcoming shower
  const nextShower = sortedShowers[0];

  // Calculate days until peak
  const getDaysUntilPeak = (shower) => {
    const now = new Date();
    let peakDate = new Date(year, shower.peak.month - 1, shower.peak.day);
    if (peakDate < now) {
      peakDate = new Date(year + 1, shower.peak.month - 1, shower.peak.day);
    }
    const diffTime = peakDate - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Check if shower is currently active
  const isActive = (shower) => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentDay = now.getDate();
    
    const startMonth = shower.active.start.month;
    const endMonth = shower.active.end.month;
    
    // Handle year wrap-around (e.g., Quadrantids: Dec 28 - Jan 12)
    if (startMonth > endMonth) {
      return (currentMonth >= startMonth || currentMonth <= endMonth);
    }
    
    if (currentMonth > startMonth && currentMonth < endMonth) return true;
    if (currentMonth === startMonth && currentDay >= shower.active.start.day) return true;
    if (currentMonth === endMonth && currentDay <= shower.active.end.day) return true;
    return false;
  };

  // Calculate radiant altitude at midnight
  const getRadiantAltitude = (shower) => {
    // Simplified calculation - radiant altitude at local midnight
    const decRad = shower.radiant.dec * Math.PI / 180;
    const latRad = latitude * Math.PI / 180;
    
    // Maximum altitude when radiant crosses meridian
    const maxAlt = 90 - Math.abs(latitude - shower.radiant.dec);
    return Math.max(0, Math.min(90, maxAlt));
  };

  const formatDate = (month, day) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[month - 1]} ${day}`;
  };

  return (
    <div className="space-y-4">
      {/* Next Shower Highlight */}
      <Card className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            {t('meteor.nextShower')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white">{nextShower.name}</h3>
              <p className="text-sm text-slate-400">{nextShower.namePL}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge className="bg-purple-500/20 text-purple-300">
                  {t('meteor.peak')} {formatDate(nextShower.peak.month, nextShower.peak.day)}
                </Badge>
                <Badge className="bg-amber-500/20 text-amber-300">
                  {getDaysUntilPeak(nextShower)} {t('meteor.days')}
                </Badge>
                {isActive(nextShower) && (
                  <Badge className="bg-emerald-500/20 text-emerald-300 animate-pulse">
                    {t('meteor.activeNow')}
                  </Badge>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-purple-400">{nextShower.zhr}</div>
              <div className="text-xs text-slate-500">{t('meteor.zhrLabel')}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shower Calendar */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Calendar className="w-4 h-4 text-cyan-400" />
            {year} {t('meteor.showerCalendar')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {sortedShowers.map((shower) => {
            const visibility = getVisibilityQuality(shower);
            const active = isActive(shower);
            const daysUntil = getDaysUntilPeak(shower);
            const radiantAlt = getRadiantAltitude(shower);
            
            return (
              <div
                key={shower.id}
                className={`p-3 rounded-lg border transition-all cursor-pointer ${
                  selectedShower?.id === shower.id 
                    ? 'border-purple-500/50 bg-purple-500/10' 
                    : 'border-white/5 bg-slate-800/30 hover:bg-slate-800/50'
                } ${active ? 'ring-1 ring-emerald-500/30' : ''}`}
                onClick={() => setSelectedShower(selectedShower?.id === shower.id ? null : shower)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      shower.zhr >= 100 ? 'bg-amber-500/20' : 
                      shower.zhr >= 50 ? 'bg-purple-500/20' : 'bg-slate-500/20'
                    }`}>
                      <Star className={`w-5 h-5 ${
                        shower.zhr >= 100 ? 'text-amber-400' : 
                        shower.zhr >= 50 ? 'text-purple-400' : 'text-slate-400'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{shower.name}</span>
                        {active && (
                          <Badge className="text-xs bg-emerald-500/20 text-emerald-300 h-5">
                            {t('meteor.active')}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {t('meteor.peak')} {formatDate(shower.peak.month, shower.peak.day)} • ZHR: {shower.zhr}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`${visibility.bg} ${visibility.color} text-xs`}>
                      {visibility.quality}
                    </Badge>
                    <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${
                      selectedShower?.id === shower.id ? 'rotate-90' : ''
                    }`} />
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedShower?.id === shower.id && (
                  <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
                    <p className="text-sm text-slate-400">{shower.descriptionPL}</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-800/50 rounded p-2">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          {t('meteor.activePeriod')}
                        </div>
                        <div className="text-sm text-white">
                          {formatDate(shower.active.start.month, shower.active.start.day)} - {formatDate(shower.active.end.month, shower.active.end.day)}
                        </div>
                      </div>

                      <div className="bg-slate-800/50 rounded p-2">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Moon className="w-3 h-3" />
                          {t('meteor.moonInterference')}
                        </div>
                        <div className={`text-sm ${visibility.color}`}>
                          {100 - visibility.score}% {t('meteor.illumination')}
                        </div>
                      </div>

                      <div className="bg-slate-800/50 rounded p-2">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Sparkles className="w-3 h-3" />
                          {t('meteor.speed')}
                        </div>
                        <div className="text-sm text-white">
                          {shower.speed} km/s
                        </div>
                      </div>

                      <div className="bg-slate-800/50 rounded p-2">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Eye className="w-3 h-3" />
                          {t('meteor.radiantAlt')}
                        </div>
                        <div className="text-sm text-white">
                          {radiantAlt.toFixed(0)}° {t('meteor.max')}
                        </div>
                      </div>
                    </div>

                    <div className="text-xs text-slate-500">
                      <strong>{t('meteor.parent')}</strong> {shower.parent}
                    </div>

                    <div className="text-xs text-slate-500">
                      <strong>{t('meteor.radiant')}</strong> RA {shower.radiant.ra}° / Dec {shower.radiant.dec}°
                    </div>

                    {daysUntil <= 30 && daysUntil > 0 && (
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded p-2 text-sm text-purple-300">
                        ⏰ {t('meteor.peakIn')} {daysUntil} {t('meteor.days')} - {t('meteor.startPlanningShoot')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Photography Tips */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading">📸 {t('meteor.photographyTips')}</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-slate-400 space-y-2">
          <p>• <strong>{t('meteor.bestTime')}</strong> {t('meteor.tipBestTimeText')}</p>
          <p>• <strong>{t('meteor.settings')}</strong> {t('meteor.tipSettingsText')}</p>
          <p>• <strong>{t('meteor.tip')}</strong> {t('meteor.tipFramingText')}</p>
          <p>• <strong>{t('meteor.stack')}</strong> {t('meteor.tipStackText')}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MeteorShowers;
